import {ChangeDetectorRef, Component, EventEmitter, Input, Output, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {BrowserSessionStateId} from '../../types/ids';
import {BrowserSessionService} from '../../services/browsersession.service';
import {StateInfoItem, StateInfoItemRendererFactory} from './stateinfoitemrendererfactory';
import {CanvasComponent} from '../canvas/canvas.component';
import {isDefined} from '../../utils/utils';
import {StatedataViewerComponent, StatedataViewerToolConfig} from '../../tools/statedataviewer/statedataviewer.component';
import {FeatureService} from '../../services/feature.service';
import {State} from '../../types/browsersession';
import {DisplacedElementIssue} from './displacedElementIssue.stateinfo';
import {ModalToolContainerService} from '../../tools/modaltoolcontainer/modaltoolcontainer.service';
import {Point} from 'paper';
import {faQuestionCircle} from "@fortawesome/pro-light-svg-icons";
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'wm-stateview',
    templateUrl: './stateview.component.html',
    styleUrls: ['./stateview.component.less']
})
export class StateViewComponent {

    @Input() stateId: BrowserSessionStateId;
    @Input() infoItems: StateInfoItem[] = [];
    @Input() noActions? = false;
    @Input() autoscroll? = false;

    state: State;
    image; //for PaperJS

    weHaveNoImage: boolean = false;

    @ViewChildren('canvas')
    canvas: QueryList<CanvasComponent>;

    @Output()
    mouseMoveEvent = new EventEmitter<{x, y}>();


    private highestY = 0;
    private lowestY = Number.MAX_SAFE_INTEGER;

    constructor(private browserSessionService: BrowserSessionService, private infoItemRendererFactory: StateInfoItemRendererFactory, private toolService: ModalToolContainerService,
                private featureService: FeatureService, private ref: ChangeDetectorRef) {
    }

    ngAfterViewInit() {
        this.canvas.changes.subscribe(canvasChange => {
            if (isDefined(canvasChange.first)) {
                this.canvas.first.removeAllLayers();
                let image = new Image();
                if (isDefined(this.state.firstScreenshotUrl)) {
                    image.src = this.state.firstScreenshotUrl;
                    let self = this;
                    image.onload = () => self.imageOnLoad(self);
                }
                this.image = image;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this.highestY = 0;
        this.lowestY = Number.MAX_SAFE_INTEGER;

        if (changes.hasOwnProperty('infoItems')) {
            if (isDefined(this.canvas) && isDefined(this.canvas.first)) {
                this.updateCanvas();
            }
        }
        if (changes.hasOwnProperty('stateId') && !!this.stateId) {
            this.browserSessionService.getState$(this.stateId).pipe(untilDestroyed(this)).subscribe(state => {
                this.state = undefined;
                this.weHaveNoImage = !state.firstScreenshotUrl;
                setTimeout(() => {
                    this.state = state;
                    this.ref.markForCheck();
                }, 0);
            });
        }
    }

    openStateViewerTool() {
        let stateDataId = this.state.associatedArtifacts
            .filter(artifact => artifact.artifactType === 'DOM.DomLayoutTree')
            .map(stateDataArtifact => stateDataArtifact.data['layoutTreeId'])[0];

        if (stateDataId) {
            this.toolService.openTool(StatedataViewerComponent, new StatedataViewerToolConfig(stateDataId, this.stateId))
        }
    }

    haveValidStatedata() {
        return !! (this.state && this.state.associatedArtifacts && this.state.associatedArtifacts.find(artifact => artifact.artifactType === 'DOM.DomLayoutTree' && !!artifact.data['layoutTreeId']));
    }

    onMouseMove(x: number, y: number) {
        this.mouseMoveEvent.emit({x: x, y: y});
    }


    imageOnLoad(self) {
        let scope = self.canvas.first.activateAndGetPaperScope();
        self.canvas.first.addAndActivateLayer("screenshot");

        let layer = new scope.Raster(self.image, new scope.Point(0, 0));

        this.updateCanvas();
    }

    private maxHeight = 4000;

    private updateCanvas() {
        let scope = this.canvas.first.activateAndGetPaperScope();
        let resultBoundingBox = new scope.Rectangle(0, 0, 0, 0);

        // Get the maximum bounding box of all layers
        let layers = scope.project.layers;
        for (let layer of layers) {
            resultBoundingBox = resultBoundingBox.unite(layer.bounds);
        }

        if (resultBoundingBox.height > this.maxHeight) {
            let resultScaling = this.maxHeight / (resultBoundingBox.height);
            resultBoundingBox = resultBoundingBox.scale(resultScaling);
        }

        // limit the maximum height of the canvas to 8000px
        scope.view.viewSize.height = resultBoundingBox.height > this.maxHeight ? this.maxHeight : resultBoundingBox.height;

        // scale the screenshot to the maximum canvas size
        let screenshotlayer = this.scaleLayerToCanvas("screenshot");

        // update the size of the canvas to the calculated image size if necessary
        if (resultBoundingBox.height < this.maxHeight && !!screenshotlayer) {
            scope.view.viewSize = screenshotlayer.bounds.size;
        }

        // render all necessary info items
        this.drawStateInfoItems();
        scope.view.draw();
    }

    private drawStateInfoItems() {
        this.canvas.first.removeLayer("activeissue");
        this.canvas.first.removeLayer("highlightedelements");
        this.canvas.first.removeLayer("text");

        let scope = this.canvas.first.activateAndGetPaperScope();
        this.canvas.first.addAndActivateLayer("activeissue");

        // draw an invisible bounding box around all issues
        let imageRectangle = new scope.Rectangle(new scope.Point(0, 0), new scope.Rectangle(this.image.width, this.image.height));
        let imageRectanglePath = new scope.Path.Rectangle(imageRectangle);

        if (!!this.infoItems) {
            this.infoItems.forEach(item => {
                let infoItemRenderer = this.infoItemRendererFactory.makeCanvasRenderer(item.type);
                infoItemRenderer.renderToCanvas(item, this.canvas.first);

                if(item instanceof DisplacedElementIssue) {
                    this.collectYs(item);
                }
            });
        }
        this.scaleLayerToCanvas("activeissue");
    }

    private collectYs(item: DisplacedElementIssue) {
        this.highestY = Math.max(this.highestY, item.properties.maxY);
        this.lowestY = Math.min(this.lowestY, Math.max(0, item.properties.minY));
    }

    private scaleLayerToCanvas(layerName) {
        let scope = this.canvas.first.activateAndGetPaperScope();

        let layer = this.canvas.first.activateAndGetLayer(layerName);
        if (!!layer) {
            layer.translate(layer.bounds.topLeft.multiply(-1));
            const widthRatio = scope.view.size.width / layer.bounds.width;
            const heightRatio = scope.view.size.height / layer.bounds.height;

            layer.scale(Math.min(heightRatio, widthRatio), new scope.Point(0, 0));
            layer.translate(new Point((scope.view.size.width - layer.bounds.width) / 2, 0));
            scope.view.draw();
        }

        if (this.autoscroll) {
            this.scrollToPosition(scope)
        }
        return layer;
    }

    private scrollToPosition(scope) {
        //give a bit leeway to both values, so we don't scroll them close to the edges of the page
        let startPosition = 300 + this.lowestY * ((scope.view.size.height) / this.image.height);
        let endPosition = 500 + this.highestY * ((scope.view.size.height) / this.image.height);

        if (this.highestY > this.lowestY &&
            !(window.pageYOffset - 100 < startPosition && window.pageYOffset + window.innerHeight > endPosition)) {

            // dirty check for IE, since we cannot properly check if the function exsdists, due to overrides
            if (window.navigator.userAgent.indexOf('MSIE ') > 0 || window.navigator.userAgent.indexOf('Trident/') > 0) {
                window.scrollTo(0, startPosition);
            } else {
                window.scrollTo({
                    top: startPosition,
                    behavior: 'smooth'
                });
            }
        }
    }

    onResized() {
        if (isDefined(this.canvas.first)) {
            this.canvas.first.resetScaling();
        }
    }


    protected readonly faQuestionCircle = faQuestionCircle;
}
